/**
 * Utility functions for localStorage and sessionStorage access
 */

import { environment } from '../../environments/environment';
import { LOCAL_STORAGE_KEYS } from '../constants/storage-keys';
import { DEFAULT_LOCALE, isSupportedLocale } from '../constants/locales';

/**
 * Resolves the environment default language, falling back to {@link DEFAULT_LOCALE} when it is
 * unset, still the unreplaced build-time placeholder, or otherwise not a supported locale.
 */
function environmentDefaultLocale(): string {
  return isSupportedLocale(environment.DEFAULT_LANGUAGE)
    ? environment.DEFAULT_LANGUAGE
    : DEFAULT_LOCALE;
}

/**
 * Gets the current UI language from localStorage, validated against the supported locales.
 *
 * Resolution order: a supported value stored in localStorage → a supported environment default →
 * {@link DEFAULT_LOCALE}. An unsupported or stale localStorage value, and the unreplaced
 * `'DEFAULT_LANGUAGE'` placeholder, never leak through — they fail the supported-locale check.
 *
 * @returns A guaranteed-supported language code (e.g. 'en', 'fr', 'ar')
 */
export function getLanguageWithFallback(): string {
  try {
    const stored = localStorage.getItem(LOCAL_STORAGE_KEYS.LANGUAGE);
    if (isSupportedLocale(stored)) {
      return stored;
    }
  } catch (e) {
    // localStorage may throw in incognito mode or with security restrictions; fall through.
  }
  return environmentDefaultLocale();
}
